/*
 * Collection page behaviour.
 *
 * <collection-page> applies filters, sorting, and pagination in place: it
 * builds the URL from the filter and sort forms (or a link's href), refreshes
 * the collection partials, and pushes the URL to history so back/forward and
 * shared links work. It also opens and closes the filter drawer and expands
 * the clamped collection description. (The subcategory row is a <scroll-row>,
 * assets/scroll-row.js.) Without JavaScript the forms and links work as
 * regular page loads.
 *
 * Results are prefetched before the click, like Horizon's filters: when a
 * mouse rests on a filter, sort option, or page link, and at once on
 * pointerdown (taps too). The prediction builds the URL the click will
 * produce; a prefetched answer is used once, within 30 seconds.
 */

const COLLECTION_PARTIALS = ['collection-count', 'filters-drawer', 'active-filters', 'collection-products'];
const PREFETCH_HOVER_DELAY = 150;
const PREFETCH_MAX_AGE = 30_000;
const PREFETCH_LIMIT = 12;

class CollectionPage extends HTMLElement {
  #abort;
  #request;
  #observers = [];
  #prefetches = new Map();
  #hovered = null;
  #hoverTimer;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.drawer = this.querySelector('#FacetsDrawer');
    this.sortForm = this.querySelector('[data-sort-form]');

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    window.addEventListener('popstate', () => this.#render(new URL(window.location.href), { push: false }), {
      signal,
    });

    this.#setUpDescription(signal);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#request?.abort();
    clearTimeout(this.#hoverTimer);
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
  }

  get #behavior() {
    return this.#reducedMotion.matches ? 'instant' : 'smooth';
  }

  #onChange = (event) => {
    const form = event.target.form;
    if (!form?.matches('[data-facets-form], [data-sort-form]')) return;

    if (form === this.sortForm) form.closest('details')?.removeAttribute('open');
    this.#render(this.#urlFromForm());
  };

  #onSubmit = (event) => {
    if (!event.target.matches('[data-facets-form], [data-sort-form]')) return;
    event.preventDefault();
    this.#render(this.#urlFromForm());
  };

  #onClick = (event) => {
    if (event.target.closest('[data-facets-open]')) {
      this.drawer?.showModal();
      return;
    }

    // Clicks on the backdrop target the dialog itself.
    if (event.target === this.drawer || event.target.closest('[data-facets-close]')) {
      this.drawer?.close();
      return;
    }

    const link = event.target.closest('a[data-facets-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;

    event.preventDefault();
    const url = new URL(link.href);
    url.searchParams.delete('section_id');
    this.#render(url, { scroll: link.hasAttribute('data-pagination-link') });
  };

  // Filters and sorting live in separate forms; combine both into one URL. For a prefetch,
  // `toggle` is a checkbox about to flip and `sort` a sort value about to be picked. Fields are
  // read in document order either way, so a prediction matches the URL of the real change.
  #urlFromForm({ toggle = null, sort = null } = {}) {
    const url = new URL(window.location.pathname, window.location.origin);
    const facets = this.querySelector('[data-facets-form]');

    for (const field of facets?.elements ?? []) {
      if (!field.name || field.disabled) continue;

      const included =
        field.type === 'checkbox' || field.type === 'radio'
          ? field === toggle
            ? !field.checked
            : field.checked
          : field.value !== '';
      if (included) url.searchParams.append(field.name, field.value);
    }

    const sortValue = sort ?? this.sortForm?.querySelector('input:checked')?.value;
    if (sortValue && sortValue !== this.sortForm.dataset.default) url.searchParams.set('sort_by', sortValue);

    return url;
  }

  // The URL a click on this filter, sort option, or link would load.
  #predictUrl(target) {
    if (target.matches('a')) {
      const url = new URL(target.href);
      url.searchParams.delete('section_id');
      return url;
    }

    const input = target.querySelector('input');
    if (!input || input.disabled || (input.checked && input.type === 'radio')) return null;
    if (input.name === 'sort_by') return this.#urlFromForm({ sort: input.value });
    if (input.type === 'checkbox') return this.#urlFromForm({ toggle: input });
    return null;
  }

  #prefetchTarget(element) {
    return element.closest?.('.facets__option, .collection-sort__option, a[data-facets-link]') ?? null;
  }

  #onPointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;

    const target = this.#prefetchTarget(event.target);
    if (target === this.#hovered) return;
    this.#hovered = target;
    clearTimeout(this.#hoverTimer);
    if (target) this.#hoverTimer = setTimeout(() => this.#prefetch(target), PREFETCH_HOVER_DELAY);
  };

  #onPointerDown = (event) => {
    const target = this.#prefetchTarget(event.target);
    if (target) this.#prefetch(target);
  };

  #prefetch(target) {
    const url = this.#predictUrl(target);
    if (!url) return;

    const key = url.toString();
    const cached = this.#prefetches.get(key);
    if (cached && Date.now() - cached.time < PREFETCH_MAX_AGE) return;

    const promise = this.#fetchResults(url);
    promise.catch(() => this.#prefetches.delete(key));
    this.#prefetches.set(key, { promise, time: Date.now() });
    if (this.#prefetches.size > PREFETCH_LIMIT) this.#prefetches.delete(this.#prefetches.keys().next().value);
  }

  // Shopify renders only this section for the URL (Section Rendering API, `section_id`), not the whole page.
  async #fetchResults(url, signal) {
    const resultsUrl = new URL(url);
    resultsUrl.searchParams.set('section_id', this.dataset.sectionId);
    return window.themeRegions.load(COLLECTION_PARTIALS, { url: resultsUrl.toString(), signal });
  }

  // A fresh prefetched answer for this URL, used once (applying moves its nodes into the page).
  #takePrefetch(url) {
    const key = url.toString();
    const cached = this.#prefetches.get(key);
    this.#prefetches.delete(key);
    return cached && Date.now() - cached.time < PREFETCH_MAX_AGE ? cached.promise : null;
  }

  async #render(url, { push = true, scroll = false } = {}) {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.setAttribute('aria-busy', 'true');

    const openGroups = new Map(
      [...this.querySelectorAll('details.facets__group[id]')].map((details) => [details.id, details.open])
    );

    const prefetched = this.#takePrefetch(url);

    try {
      // Without the regions (assets/regions.js) in the answer, fall back to a full page load.
      let response = prefetched ? await prefetched.catch(() => null) : null;
      if (request.signal.aborted) return;
      response ??= await this.#fetchResults(url, request.signal);
      if (response.size === 0) throw new Error('No result regions');
      window.themeRegions.apply(response);
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url);
      return;
    } finally {
      if (this.#request === request) this.removeAttribute('aria-busy');
    }

    this.#syncControls(url, openGroups);
    if (push) window.history.pushState({}, '', url);
    if (scroll) {
      // A new page: keyboard and screen reader users continue from its first product.
      this.querySelector('[data-results]')?.scrollIntoView({ block: 'start', behavior: this.#behavior });
      this.querySelector('.product-grid a[href]')?.focus({ preventScroll: true });
    }
  }

  // apply() keeps the previous form values and re-renders disclosures open or closed by default:
  // reset inputs to what the server rendered for this URL and restore the shopper's open groups.
  #syncControls(url, openGroups) {
    this.querySelectorAll('[data-facets-form] input').forEach((input) => {
      if (input.type === 'checkbox') {
        input.checked = input.defaultChecked;
      } else {
        input.value = input.defaultValue;
      }
    });

    if (this.sortForm) {
      const sort = url.searchParams.get('sort_by') || this.sortForm.dataset.default;
      this.sortForm.querySelectorAll('input[name="sort_by"]').forEach((input) => (input.checked = input.value === sort));
    }

    openGroups.forEach((open, id) => {
      const details = document.getElementById(id);
      if (details) details.open = open;
    });
  }

  // Shows the expand toggle only when the description is clamped.
  #setUpDescription(signal) {
    const text = this.querySelector('.collection__description-text');
    const toggle = this.querySelector('.collection__description-toggle');
    if (!text || !toggle) return;

    // The server's guess (shown, phone-only, or hidden) usually matches already; measuring settles it.
    const update = () => {
      if (toggle.getAttribute('aria-expanded') === 'true') return;
      toggle.classList.remove('collection__description-toggle--phone');
      toggle.hidden = text.scrollHeight <= text.clientHeight + 1;
    };

    toggle.addEventListener(
      'click',
      () => {
        const expanded = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.textContent = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
        text.classList.toggle('is-expanded', expanded);
      },
      { signal }
    );

    const observer = new ResizeObserver(update);
    observer.observe(text);
    this.#observers.push(observer);
  }

}

if (!customElements.get('collection-page')) customElements.define('collection-page', CollectionPage);
